import styled, { keyframes } from 'styled-components'

const pulseGlow = keyframes`
  0% {
    box-shadow: 0 0 0 0 rgba(166, 193, 11, 0.55), 0 8px 20px rgba(0, 24, 66, 0.45);
  }
  70% {
    box-shadow: 0 0 0 12px rgba(166, 193, 11, 0), 0 8px 20px rgba(0, 24, 66, 0.45);
  }
  100% {
    box-shadow: 0 0 0 0 rgba(166, 193, 11, 0), 0 8px 20px rgba(0, 24, 66, 0.45);
  }
`

const typingBounce = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.4;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`

const slideInDrawer = keyframes`
  from {
    transform: translateX(100%);
  }
  to {
    transform: translateX(0);
  }
`

export const FloatingWidgetWrapper = styled.div`
  position: fixed;
  right: 1.25rem;
  z-index: 50000;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.35rem;
  user-select: none;
  touch-action: none;
`

export const DragIndicatorGrip = styled.div`
  width: 28px;
  height: 12px;
  background: ${({ theme }) => (theme.name === 'light' ? 'rgba(255, 255, 255, 0.92)' : 'rgba(15, 23, 42, 0.85)')};
  backdrop-filter: blur(4px);
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : '#334155')};
  border-radius: 999px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  cursor: grab;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.12);
  transition: all 0.2s;

  &:hover {
    border-color: #3b82f6;
    transform: scale(1.06);
  }

  &:active {
    cursor: grabbing;
  }
`

export const GripDot = styled.span`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background: #94a3b8;
`

export const FloatingBubbleButton = styled.button`
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2.5px solid #a6c10b;
  background: radial-gradient(circle at 35% 30%, #002b80 0%, #001842 100%);
  padding: 0;
  cursor: pointer;
  box-shadow: 0 10px 25px -5px rgba(0, 24, 66, 0.65), 0 0 14px rgba(166, 193, 11, 0.35);
  animation: ${pulseGlow} 3s infinite;
  transition: transform 0.2s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: visible;

  &:hover {
    transform: scale(1.07);
    box-shadow: 0 14px 30px -5px rgba(0, 24, 66, 0.75), 0 0 18px rgba(166, 193, 11, 0.55);
  }

  &:active {
    transform: scale(0.96);
  }
`

export const BubbleAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
  pointer-events: none;
`

export const OnlineDotBadge = styled.span`
  position: absolute;
  bottom: 1px;
  right: 1px;
  width: 14px;
  height: 14px;
  background: #10b981;
  border: 2.5px solid #ffffff;
  border-radius: 50%;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`

export const FloatingTooltip = styled.div`
  position: absolute;
  right: 72px;
  top: 50%;
  transform: translateY(-50%);
  background: #0f172a;
  color: #ffffff;
  padding: 0.45rem 0.85rem;
  border-radius: 0.6rem;
  font-size: 0.78rem;
  font-weight: 600;
  white-space: nowrap;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.25);
  pointer-events: none;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  border: 1px solid rgba(255, 255, 255, 0.12);

  &::after {
    content: '';
    position: absolute;
    right: -5px;
    top: 50%;
    transform: translateY(-50%) rotate(45deg);
    width: 10px;
    height: 10px;
    background: #0f172a;
    border-top: 1px solid rgba(255, 255, 255, 0.12);
    border-right: 1px solid rgba(255, 255, 255, 0.12);
  }
`

export const DrawerBackdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.38);
  backdrop-filter: blur(2px);
  z-index: 50010;
`

export const DrawerContainer = styled.aside<{ $isExpanded?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: ${({ $isExpanded }) => ($isExpanded ? '720px' : '430px')};
  max-width: 100vw;
  height: 100dvh;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0f172a')};
  border-left: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#1e293b')};
  box-shadow: -12px 0 35px -5px rgba(15, 23, 42, 0.25);
  z-index: 50020;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: ${slideInDrawer} 0.24s cubic-bezier(0.16, 1, 0.3, 1);
  transition: width 0.28s cubic-bezier(0.16, 1, 0.3, 1);
`

export const DrawerHeader = styled.div`
  padding: 0.9rem 1.15rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0b132b')};
  border-bottom: 1px solid ${({ theme }) => (theme.name === 'light' ? '#f1f5f9' : '#1e293b')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
`

export const HeaderProfile = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`

export const HeaderAvatarWrapper = styled.div`
  position: relative;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  padding: 2px;
  background: linear-gradient(135deg, #a6c10b, #38bdf8);
  flex-shrink: 0;
`

export const HeaderAvatarImg = styled.img`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0f172a')};
`

export const HeaderTitleGroup = styled.div`
  display: flex;
  flex-direction: column;
`

export const HeaderNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
`

export const AssistantName = styled.h3`
  font-size: 0.96rem;
  font-weight: 700;
  color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  margin: 0;
  letter-spacing: -0.01em;
`

export const StatusPill = styled.span<{ $locked?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.65rem;
  font-weight: 700;
  color: ${({ $locked }) => ($locked ? '#d97706' : '#059669')};
  background: ${({ $locked }) => ($locked ? 'rgba(245, 158, 11, 0.14)' : 'rgba(16, 185, 129, 0.14)')};
  padding: 0.12rem 0.45rem;
  border-radius: 999px;
  text-transform: uppercase;
  letter-spacing: 0.03em;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ $locked }) => ($locked ? '#f59e0b' : '#10b981')};
  }
`

export const AssistantSubtitle = styled.span`
  font-size: 0.74rem;
  color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
  font-weight: 500;
`

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 0.3rem;
`

export const HeaderIconButton = styled.button<{ $active?: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 0.5rem;
  border: 1px solid
    ${({ $active, theme }) =>
      $active ? '#bfdbfe' : theme.name === 'light' ? 'transparent' : 'rgba(255,255,255,0.06)'};
  background: ${({ $active, theme }) =>
    $active
      ? 'rgba(37, 99, 235, 0.12)'
      : theme.name === 'light'
        ? '#f8fafc'
        : 'rgba(30, 41, 59, 0.7)'};
  color: ${({ $active, theme }) =>
    $active ? '#2563eb' : theme.name === 'light' ? '#64748b' : '#cbd5e1'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => (theme.name === 'light' ? '#f1f5f9' : '#1e293b')};
    color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#ffffff')};
  }
`

export const ContextBanner = styled.div`
  margin: 0.65rem 1.1rem 0.25rem;
  padding: 0.55rem 0.8rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : '#1e293b')};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#334155')};
  border-radius: 0.65rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const ContextBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.74rem;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'light' ? '#334155' : '#e2e8f0')};

  svg {
    color: #2563eb;
    flex-shrink: 0;
  }
`

export const ContextModelTag = styled.span`
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.15rem 0.5rem;
  border-radius: 999px;
  background: rgba(37, 99, 235, 0.12);
  color: #3b82f6;
  border: 1px solid rgba(59, 130, 246, 0.25);
  max-width: 165px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const TasksActionBar = styled.div`
  padding: 0.45rem 1.1rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0f172a')};
  border-bottom: 1px solid ${({ theme }) => (theme.name === 'light' ? '#f1f5f9' : '#1e293b')};
  display: flex;
  align-items: center;
  gap: 0.4rem;
  overflow-x: auto;

  &::-webkit-scrollbar {
    height: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 999px;
  }
`

export const TaskActionBtn = styled.button<{ $variant?: 'pdf' | 'excel' | 'sellout' | 'audit' }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.34rem 0.65rem;
  border-radius: 0.5rem;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.15s ease;
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'pdf'
        ? 'rgba(239, 68, 68, 0.3)'
        : $variant === 'excel'
          ? 'rgba(22, 163, 74, 0.3)'
          : $variant === 'sellout'
            ? 'rgba(2, 132, 199, 0.3)'
            : 'rgba(139, 92, 246, 0.3)'};
  background: ${({ $variant }) =>
    $variant === 'pdf'
      ? 'rgba(239, 68, 68, 0.08)'
      : $variant === 'excel'
        ? 'rgba(22, 163, 74, 0.08)'
        : $variant === 'sellout'
          ? 'rgba(2, 132, 199, 0.08)'
          : 'rgba(139, 92, 246, 0.08)'};
  color: ${({ $variant, theme }) =>
    $variant === 'pdf'
      ? theme.name === 'light'
        ? '#dc2626'
        : '#f87171'
      : $variant === 'excel'
        ? theme.name === 'light'
          ? '#16a34a'
          : '#4ade80'
        : $variant === 'sellout'
          ? theme.name === 'light'
            ? '#0284c7'
            : '#38bdf8'
          : theme.name === 'light'
            ? '#7c3aed'
            : '#c084fc'};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 3px 8px rgba(15, 23, 42, 0.08);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`

export const TaskProgressCard = styled.div<{ $done?: boolean }>`
  background: ${({ theme, $done }) =>
    $done
      ? theme.name === 'light'
        ? '#ecfdf5'
        : 'rgba(16, 185, 129, 0.14)'
      : theme.name === 'light'
      ? '#eff6ff'
      : 'rgba(37, 99, 235, 0.16)'};
  border: 1px solid
    ${({ theme, $done }) =>
      $done
        ? theme.name === 'light'
          ? '#a7f3d0'
          : 'rgba(16, 185, 129, 0.4)'
        : theme.name === 'light'
        ? '#bfdbfe'
        : 'rgba(59, 130, 246, 0.4)'};
  border-radius: 0.75rem;
  padding: 0.7rem 0.9rem;
  display: flex;
  flex-direction: column;
  gap: 0.42rem;
  font-size: 0.78rem;
  color: ${({ theme, $done }) =>
    $done
      ? theme.name === 'light'
        ? '#065f46'
        : '#6ee7b7'
      : theme.name === 'light'
      ? '#1e40af'
      : '#93c5fd'};
  font-weight: 600;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.08);
  transition: all 0.25s ease;

  @keyframes gisaSpin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }

  .spin-icon {
    animation: gisaSpin 1s linear infinite;
    flex-shrink: 0;
  }
`

export const TaskProgressHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
`

export const TaskProgressTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  flex: 1;

  span {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`

export const TaskProgressPctBadge = styled.span<{ $done?: boolean }>`
  font-variant-numeric: tabular-nums;
  font-family: monospace;
  font-size: 0.73rem;
  font-weight: 800;
  padding: 0.14rem 0.48rem;
  border-radius: 999px;
  background: ${({ theme, $done }) =>
    $done
      ? theme.name === 'light'
        ? '#10b981'
        : 'rgba(16, 185, 129, 0.25)'
      : theme.name === 'light'
      ? '#2563eb'
      : 'rgba(37, 99, 235, 0.32)'};
  color: ${({ theme, $done }) =>
    $done
      ? theme.name === 'light'
        ? '#ffffff'
        : '#6ee7b7'
      : theme.name === 'light'
      ? '#ffffff'
      : '#bfdbfe'};
  flex-shrink: 0;
`

export const TaskProgressBarTrack = styled.div`
  width: 100%;
  height: 7px;
  border-radius: 999px;
  background: ${({ theme }) =>
    theme.name === 'light' ? 'rgba(37, 99, 235, 0.14)' : 'rgba(15, 23, 42, 0.75)'};
  overflow: hidden;
  position: relative;
`

export const TaskProgressBarFill = styled.div<{ $progress: number; $done?: boolean }>`
  width: ${({ $progress }) => Math.max(4, Math.min(100, $progress))}%;
  height: 100%;
  border-radius: 999px;
  background: ${({ $done }) =>
    $done
      ? 'linear-gradient(90deg, #10b981 0%, #34d399 100%)'
      : 'linear-gradient(90deg, #0284c7 0%, #38bdf8 55%, #10b981 100%)'};
  transition: width 0.28s cubic-bezier(0.22, 1, 0.36, 1);
  position: relative;
  overflow: hidden;

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      rgba(255, 255, 255, 0) 0%,
      rgba(255, 255, 255, 0.38) 50%,
      rgba(255, 255, 255, 0) 100%
    );
    animation: gisaShimmer 1.3s infinite;
  }

  @keyframes gisaShimmer {
    0% {
      transform: translateX(-100%);
    }
    100% {
      transform: translateX(100%);
    }
  }
`

export const TaskProgressStepDetail = styled.div`
  font-size: 0.7rem;
  font-weight: 500;
  color: ${({ theme }) => (theme.name === 'light' ? '#475569' : '#94a3b8')};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
`

export const ChatMessagesScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem 1.15rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0f172a')};

  &::-webkit-scrollbar {
    width: 5px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : '#334155')};
    border-radius: 999px;
  }
`

export const WelcomeDashboard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-bottom: 0.35rem;
`

export const WelcomeHero = styled.div`
  background: ${({ theme }) =>
    theme.name === 'light'
      ? 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 100%)'
      : 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#334155')};
  border-radius: 0.85rem;
  padding: 0.9rem 1rem;

  h3 {
    margin: 0 0 0.25rem 0;
    font-size: 0.92rem;
    font-weight: 700;
    color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#f8fafc')};
    display: flex;
    align-items: center;
    gap: 0.4rem;
  }

  p {
    margin: 0;
    font-size: 0.76rem;
    color: ${({ theme }) => (theme.name === 'light' ? '#475569' : '#94a3b8')};
    line-height: 1.45;
  }
`

export const CapabilitiesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.55rem;

  @media (max-width: 420px) {
    grid-template-columns: 1fr;
  }
`

export const CapabilityCard = styled.button<{ $color?: string }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
  gap: 0.4rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#1e293b')};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#334155')};
  cursor: pointer;
  transition: all 0.18s ease;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 3px;
    height: 100%;
    background: ${({ $color }) => $color || '#2563eb'};
    opacity: 0.85;
  }

  &:hover {
    border-color: ${({ $color }) => $color || '#3b82f6'};
    background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : '#26354d')};
    transform: translateY(-2px);
    box-shadow: 0 6px 14px -3px rgba(15, 23, 42, 0.12);
  }
`

export const CapabilityIconBox = styled.div<{ $color?: string }>`
  width: 30px;
  height: 30px;
  border-radius: 0.5rem;
  background: ${({ $color }) => ($color ? `${$color}18` : '#eff6ff')};
  color: ${({ $color }) => $color || '#2563eb'};
  display: flex;
  align-items: center;
  justify-content: center;
`

export const CapabilityContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;

  .cap-title {
    font-size: 0.77rem;
    font-weight: 700;
    color: ${({ theme }) => (theme.name === 'light' ? '#1e293b' : '#f1f5f9')};
  }

  .cap-desc {
    font-size: 0.69rem;
    color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
    line-height: 1.35;
  }
`

export const MessageBubbleRow = styled.div<{ $isUser?: boolean }>`
  display: flex;
  align-items: flex-start;
  justify-content: ${({ $isUser }) => ($isUser ? 'flex-end' : 'flex-start')};
  gap: 0.6rem;
  width: 100%;
  min-width: 0;
`

export const MiniAvatarImg = styled.img`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  margin-top: 2px;
  border: 1.5px solid #a6c10b;
  background: #001842;
`

export const MessageCard = styled.div<{ $isUser?: boolean }>`
  width: ${({ $isUser }) => ($isUser ? 'auto' : '100%')};
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  overflow-wrap: break-word;
  word-break: break-word;
  padding: 0.75rem 0.95rem;
  border-radius: ${({ $isUser }) =>
    $isUser ? '0.9rem 0.9rem 0.2rem 0.9rem' : '0.2rem 0.9rem 0.9rem 0.9rem'};
  background: ${({ $isUser, theme }) =>
    $isUser ? '#2563eb' : theme.name === 'light' ? '#f8fafc' : '#1e293b'};
  color: ${({ $isUser, theme }) =>
    $isUser ? '#ffffff' : theme.name === 'light' ? '#1e293b' : '#f1f5f9'};
  border: ${({ $isUser, theme }) =>
    $isUser ? 'none' : `1px solid ${theme.name === 'light' ? '#e2e8f0' : '#334155'}`};
  font-size: 0.82rem;
  line-height: 1.55;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);

  p {
    margin: 0 0 0.45rem 0;
    &:last-child {
      margin-bottom: 0;
    }
  }

  strong {
    font-weight: 700;
    color: ${({ $isUser, theme }) =>
      $isUser ? '#ffffff' : theme.name === 'light' ? '#0f172a' : '#38bdf8'};
  }

  li {
    margin-left: 1rem;
    margin-bottom: 0.25rem;
  }

  code {
    background: ${({ $isUser, theme }) =>
      $isUser ? 'rgba(255,255,255,0.2)' : theme.name === 'light' ? '#e2e8f0' : '#0f172a'};
    padding: 0.1rem 0.3rem;
    border-radius: 0.25rem;
    font-size: 0.76rem;
    font-family: monospace;
  }
`

export const MessageActionsToolbar = styled.div`
  margin-top: 0.75rem;
  padding-top: 0.65rem;
  border-top: 1px dashed ${({ theme }) => (theme.name === 'light' ? '#cbd5e1' : '#334155')};
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.45rem;
`

export const MessageExportButton = styled.button<{ $variant?: 'excel' | 'pdf' | 'chart' }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.38rem;
  min-height: 34px;
  padding: 0.4rem 0.72rem;
  border-radius: 0.5rem;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.16s ease;

  background: ${({ $variant, theme }) => {
    if ($variant === 'excel') {
      return 'linear-gradient(135deg, #059669 0%, #10b981 100%)'
    }
    if ($variant === 'pdf') {
      return theme.name === 'light' ? '#fef2f2' : 'rgba(239, 68, 68, 0.16)'
    }
    return theme.name === 'light' ? '#f0f9ff' : 'rgba(14, 165, 233, 0.16)'
  }};

  color: ${({ $variant, theme }) => {
    if ($variant === 'excel') return '#ffffff'
    if ($variant === 'pdf') return theme.name === 'light' ? '#b91c1c' : '#fca5a5'
    return theme.name === 'light' ? '#0369a1' : '#7dd3fc'
  }};

  border: ${({ $variant, theme }) => {
    if ($variant === 'excel') return '1px solid #059669'
    if ($variant === 'pdf') {
      return `1px solid ${theme.name === 'light' ? '#fecaca' : 'rgba(239, 68, 68, 0.38)'}`
    }
    return `1px solid ${theme.name === 'light' ? '#bae6fd' : 'rgba(56, 189, 248, 0.38)'}`
  }};

  box-shadow: ${({ $variant }) =>
    $variant === 'excel' ? '0 3px 10px rgba(16, 185, 129, 0.22)' : 'none'};

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.05);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  @media (max-width: 640px) {
    min-height: 44px;
    padding: 0.5rem 0.85rem;
  }
`

export const ActionButtonInChat = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 34px;
  gap: 0.4rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#eff6ff' : 'rgba(37, 99, 235, 0.2)')};
  color: ${({ theme }) => (theme.name === 'light' ? '#1d4ed8' : '#60a5fa')};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#bfdbfe' : 'rgba(59, 130, 246, 0.4)')};
  padding: 0.4rem 0.72rem;
  border-radius: 0.5rem;
  font-size: 0.74rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: ${({ theme }) => (theme.name === 'light' ? '#dbeafe' : 'rgba(37, 99, 235, 0.32)')};
  }

  @media (max-width: 640px) {
    min-height: 44px;
    padding: 0.5rem 0.85rem;
  }
`

export const FollowUpsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
  margin-top: 0.5rem;
`

export const FollowUpChip = styled.button`
  text-align: left;
  padding: 0.36rem 0.65rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0f172a')};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#334155')};
  border-radius: 999px;
  font-size: 0.72rem;
  font-weight: 600;
  color: ${({ theme }) => (theme.name === 'light' ? '#334155' : '#cbd5e1')};
  cursor: pointer;
  transition: all 0.15s;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : '#1e293b')};
    border-color: #3b82f6;
    color: #2563eb;
  }

  svg {
    color: #3b82f6;
    flex-shrink: 0;
  }
`

export const TypingIndicator = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0.65rem 0.9rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : '#1e293b')};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#334155')};
  border-radius: 0.2rem 0.85rem 0.85rem 0.85rem;
`

export const TypingDot = styled.span<{ $delay?: string }>`
  width: 6px;
  height: 6px;
  background: #64748b;
  border-radius: 50%;
  animation: ${typingBounce} 1.4s infinite ease-in-out both;
  animation-delay: ${({ $delay }) => $delay || '0s'};
`

export const DrawerFooter = styled.div`
  padding: 0.85rem 1.15rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0b132b')};
  border-top: 1px solid ${({ theme }) => (theme.name === 'light' ? '#f1f5f9' : '#1e293b')};
  position: relative;
`

export const ChatInputForm = styled.form`
  display: flex;
  align-items: center;
  gap: 0.45rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#f8fafc' : '#1e293b')};
  border: 1.5px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#334155')};
  border-radius: 0.75rem;
  padding: 0.35rem 0.4rem 0.35rem 0.65rem;
  transition: all 0.2s;

  &:focus-within {
    background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#0f172a')};
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.12);
  }
`

export const SlashCommandButton = styled.button<{ $active?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0.25rem 0.5rem;
  border-radius: 0.4rem;
  font-size: 0.73rem;
  font-weight: 800;
  font-family: monospace;
  border: 1px solid ${({ $active, theme }) => ($active ? '#2563eb' : theme.name === 'light' ? '#cbd5e1' : '#475569')};
  background: ${({ $active, theme }) => ($active ? '#2563eb' : theme.name === 'light' ? '#f1f5f9' : '#0f172a')};
  color: ${({ $active, theme }) => ($active ? '#ffffff' : theme.name === 'light' ? '#475569' : '#cbd5e1')};
  cursor: pointer;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    border-color: #2563eb;
    color: ${({ $active }) => ($active ? '#ffffff' : '#2563eb')};
  }
`

export const SlashPaletteMenu = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 1.15rem;
  right: 1.15rem;
  background: ${({ theme }) => (theme.name === 'light' ? '#ffffff' : '#1e293b')};
  border: 1px solid ${({ theme }) => (theme.name === 'light' ? '#e2e8f0' : '#334155')};
  border-radius: 0.85rem;
  box-shadow: 0 12px 30px -5px rgba(15, 23, 42, 0.28);
  padding: 0.5rem;
  z-index: 30;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  max-height: 290px;
  overflow-y: auto;
`

export const SlashPaletteItem = styled.button`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  width: 100%;
  padding: 0.55rem 0.65rem;
  border: none;
  background: transparent;
  border-radius: 0.55rem;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;

  &:hover {
    background: ${({ theme }) => (theme.name === 'light' ? '#f1f5f9' : '#0f172a')};
  }
`

export const SlashPaletteItemIcon = styled.div<{ $color?: string }>`
  width: 30px;
  height: 30px;
  border-radius: 0.45rem;
  background: ${({ $color }) => ($color ? `${$color}18` : '#eff6ff')};
  color: ${({ $color }) => $color || '#2563eb'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

export const SlashPaletteItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`

export const SlashPaletteItemCommand = styled.span`
  font-size: 0.78rem;
  font-weight: 700;
  color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  display: flex;
  align-items: center;
  gap: 0.4rem;
`

export const SlashPaletteItemDesc = styled.span`
  font-size: 0.7rem;
  color: ${({ theme }) => (theme.name === 'light' ? '#64748b' : '#94a3b8')};
`

export const ChatInputField = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  font-size: 0.82rem;
  color: ${({ theme }) => (theme.name === 'light' ? '#0f172a' : '#f8fafc')};
  outline: none;

  &::placeholder {
    color: #94a3b8;
  }
`

export const SendMessageButton = styled.button<{ $hasText?: boolean }>`
  width: 34px;
  height: 34px;
  border-radius: 0.55rem;
  border: none;
  background: ${({ $hasText, theme }) =>
    $hasText ? '#2563eb' : theme.name === 'light' ? '#e2e8f0' : '#334155'};
  color: ${({ $hasText }) => ($hasText ? '#ffffff' : '#94a3b8')};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${({ $hasText }) => ($hasText ? 'pointer' : 'default')};
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: ${({ $hasText, theme }) =>
      $hasText ? '#1d4ed8' : theme.name === 'light' ? '#e2e8f0' : '#334155'};
  }
`
